import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Badge } from 'react-bootstrap';
import { TrendingUp, TrendingDown, AttachMoney, Inventory } from '@mui/icons-material';
import { colors } from '../theme';

const PricingDashboard = () => {
  const [stats, setStats] = useState({
    totalProjects: 5,
    totalParts: 237,
    averageSavings: 15.3,
    topDistributor: 'DigiKey'
  });

  const [recentProjects] = useState([
    {
      id: '1',
      name: 'Power Supply Board v2',
      parts: 45,
      totalCost: 1234.56,
      savings: 186.20,
      date: '2024-01-15'
    },
    {
      id: '2',
      name: 'IoT Sensor Module',
      parts: 28,
      totalCost: 567.89,
      savings: 45.30,
      date: '2024-01-14'
    },
    {
      id: '3',
      name: 'LED Controller Rev3',
      parts: 62,
      totalCost: 2340.10,
      savings: 312.45,
      date: '2024-01-12'
    }
  ]);

  const [priceAlerts] = useState([
    {
      part: 'STM32F103C8T6',
      previousPrice: 4.50,
      currentPrice: 3.95,
      change: -12.2,
      distributor: 'Mouser'
    },
    {
      part: 'LM317T',
      previousPrice: 0.85,
      currentPrice: 0.92,
      change: 8.2,
      distributor: 'DigiKey'
    },
    {
      part: 'ESP32-WROOM-32',
      previousPrice: 3.20,
      currentPrice: 2.85,
      change: -10.9,
      distributor: 'Arrow'
    }
  ]);

  return (
    <Container>
      <Row className="mb-4">
        <Col>
          <h2>Pricing Dashboard</h2>
          <p className="text-muted">Monitor pricing trends and cost optimization opportunities</p>
        </Col>
      </Row>

      {/* Stats Cards */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <Inventory style={{ fontSize: 40, color: colors.primary }} />
              <h5 className="mt-2">Total Projects</h5>
              <h3>{stats.totalProjects}</h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <AttachMoney style={{ fontSize: 40, color: colors.success }} />
              <h5 className="mt-2">Parts Analyzed</h5>
              <h3>{stats.totalParts}</h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <TrendingDown style={{ fontSize: 40, color: colors.accent }} />
              <h5 className="mt-2">Avg. Savings</h5>
              <h3>{stats.averageSavings}%</h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <TrendingUp style={{ fontSize: 40, color: colors.warningText }} />
              <h5 className="mt-2">Best Distributor</h5>
              <h3>{stats.topDistributor}</h3>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col lg={8}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">Recent BOM Projects</h5>
            </Card.Header>
            <Card.Body>
              <Table responsive hover>
                <thead>
                  <tr>
                    <th>Project Name</th>
                    <th>Parts</th>
                    <th>Total Cost</th>
                    <th>Savings</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {recentProjects.map(project => (
                    <tr key={project.id}>
                      <td>
                        <a href={`/analysis/${project.id}`}>{project.name}</a>
                      </td>
                      <td>{project.parts}</td>
                      <td>${project.totalCost.toFixed(2)}</td>
                      <td>
                        <Badge bg="success">
                          ${project.savings.toFixed(2)}
                        </Badge>
                      </td>
                      <td>{project.date}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>

          <Card className="mt-4">
            <Card.Header>
              <h5 className="mb-0">Distributor Performance</h5>
            </Card.Header>
            <Card.Body>
              <Table responsive>
                <thead>
                  <tr>
                    <th>Distributor</th>
                    <th>Best Price Count</th>
                    <th>Avg. Lead Time</th>
                    <th>Availability Rate</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>DigiKey</td>
                    <td><Badge bg="primary">42</Badge></td>
                    <td>2-3 days</td>
                    <td>96%</td>
                  </tr>
                  <tr>
                    <td>Mouser</td>
                    <td><Badge bg="primary">38</Badge></td>
                    <td>3-4 days</td>
                    <td>94%</td>
                  </tr>
                  <tr>
                    <td>Arrow</td>
                    <td><Badge bg="primary">25</Badge></td>
                    <td>5-7 days</td>
                    <td>88%</td>
                  </tr>
                  <tr>
                    <td>Newark</td>
                    <td><Badge bg="primary">18</Badge></td>
                    <td>4-5 days</td>
                    <td>85%</td>
                  </tr>
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">Price Alerts</h5>
            </Card.Header>
            <Card.Body>
              {priceAlerts.map((alert, index) => (
                <div key={index} className="mb-3 pb-3 border-bottom">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <strong>{alert.part}</strong>
                      <div className="small text-muted">{alert.distributor}</div>
                    </div>
                    <Badge bg={alert.change < 0 ? 'success' : 'danger'}>
                      {alert.change < 0 ? <TrendingDown /> : <TrendingUp />}
                      {Math.abs(alert.change)}%
                    </Badge>
                  </div>
                  <div className="small mt-1">
                    ${alert.previousPrice.toFixed(2)} → ${alert.currentPrice.toFixed(2)}
                  </div>
                </div>
              ))}
            </Card.Body>
          </Card>

          <Card className="mt-3">
            <Card.Header>
              <h5 className="mb-0">Cost Optimization Tips</h5>
            </Card.Header>
            <Card.Body>
              <ul className="small mb-0">
                <li>Consider volume pricing for quantities above 100 units</li>
                <li>Check alternative parts for 15 components in your recent BOMs</li>
                <li>3 parts have better pricing at alternative distributors</li>
                <li>Bundle orders to reduce shipping costs</li>
              </ul>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default PricingDashboard;